import * as React from 'react';
import { useState } from 'react';
import { Button as MatButton, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { Dialog } from '../dialog/dialog';
import { useRowOptionsIconOnly } from '../row-options-context';
import { ICON_ACCENT } from '../action-buttons/icon-accent';

export type ButtonColor = 'success' | 'warning' | 'primary' | 'secondary' | 'info' | undefined;

export interface ButtonProps {
  dataTestId?: string;
  label?: string;
  color?: ButtonColor;
  variant?: 'text' | 'outlined' | 'contained';
  separated?: boolean;
  onClick: () => Promise<any> | void;
  confirmationMsg?: string;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  icon?: JSX.Element;
  tooltip?: string;
  forceConfirmation?: boolean;
  isDisabled?: boolean;
  forceLoadingSign?: boolean;
  /** Icon with the label in a tooltip. Defaults to on inside a table's row option cell, see row-options-context. */
  iconOnly?: boolean;
}

export const Button = (props: ButtonProps) => {
  const inRowOptions = useRowOptionsIconOnly();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  async function handleCallCallback() {
    setLoading(true);
    await props.onClick();
    setLoading(false);
  }

  function handleCloseOk() {
    setOpen(false);
    handleCallCallback();
  }

  function mapToMUIColor(style: ButtonColor): 'success' | 'error' | 'primary' | 'secondary' | 'info' | undefined {
    if (style === 'warning') {
      return 'error';
    }
    return style;
  }

  function handleTriggerClick(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    return props.color === 'warning' || props.forceConfirmation ? setOpen(true) : handleCallCallback();
  }

  const dataTestId = props.dataTestId ? props.dataTestId : props.label?.replaceAll(' ', '-') + '-btn';

  // A label with no icon would collapse into an empty icon button, so those stay as they are.
  const iconOnly = props.iconOnly ?? (inRowOptions && (props.icon ?? props.startIcon) != null);

  // Without the label beside it the icon is the only thing naming the action, so the name leads and the
  // tooltip - often a whole warning sentence - follows it instead of replacing it.
  const iconTitle =
    props.label && props.tooltip && props.tooltip !== props.label ? (
      <>
        {props.label}
        <br />
        {props.tooltip}
      </>
    ) : (
      (props.label ?? props.tooltip ?? '')
    );

  const button = iconOnly ? (
    <Tooltip title={iconTitle} enterDelay={0} enterNextDelay={400}>
      <span>
        <IconButton
          aria-label={props.label ?? props.tooltip}
          data-test-id={dataTestId}
          size='small'
          color={mapToMUIColor(props.color)}
          // MUI icons inherit currentColor, so an unpainted icon button sits in IconButton's grey right next to
          // our gold SVGs. One color for every icon in the cell; a warning button keeps MUI's red.
          sx={props.color === 'warning' ? undefined : { color: ICON_ACCENT, '&.Mui-disabled': { color: ICON_ACCENT, opacity: 0.4 } }}
          onClick={handleTriggerClick}
          loading={props.forceLoadingSign || loading}
          disabled={props.isDisabled}
        >
          {props.icon ?? props.startIcon}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <MatButton
      data-test-id={dataTestId}
      variant={props.variant}
      color={mapToMUIColor(props.color)}
      onClick={handleTriggerClick}
      sx={{ marginLeft: props.separated ? '3rem' : '0' }}
      loading={props.forceLoadingSign || loading}
      size='small'
      startIcon={props.startIcon}
      endIcon={props.endIcon}
      disabled={props.isDisabled}
    >
      {props.icon}
      {props.label}
    </MatButton>
  );

  return (
    <React.Fragment>
      {!iconOnly && props.tooltip ? (
        <Tooltip title={props.tooltip} enterDelay={800} enterNextDelay={800}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t('Button.confirmationTitle')}</DialogTitle>
        <DialogContent className={styles['dialog']}>
          <DialogContentText>{props.confirmationMsg !== undefined ? props.confirmationMsg : t('Button.confirmationMsg')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MatButton data-test-id={`${props.label?.replaceAll(' ', '-')}-cancel`} autoFocus onClick={() => setOpen(false)}>
            {t('Button.confirmationCancel')}
          </MatButton>
          <MatButton data-test-id={`${props.label?.replaceAll(' ', '-')}-ok`} onClick={handleCloseOk} color={'error'}>
            {t('Button.confirmationProceed')}
          </MatButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
